import Image from '@base/Image';
import styled from '@emotion/styled';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import pushRoute from '@utils/pushRoute';
import useTopology from '@api/hooks/topology/useTopology';
import Typography from 'aquilla/core/atoms/Typography';
import { segment } from '@actions/analytics';
import {
  CENTRE_CITY_SELECTED,
  OFFLINE_CENTRE_SOURCE
} from '@cont/CitySpecificOfflineCentres/constants';
import useOfflineCentreStore from '@stores/city-specific-offline-centres';
import { useRouter } from 'next/router';
import { getPathName, STATUS } from '@cont/CitySpecificOfflineCentres/utils';
import {
  fetchOfflineSelectedCentre,
  getOfflineCentreDetails
} from '@api/fetchers/citySpecificOfflineCentre';
import RightChevron from 'aquilla/icons/actions/RightChevron';
import segmentStorage from '@events/segmentStorage';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMeUid } from '@api/selectors/auth';

const Container = styled.div`
  padding: ${Spacings.SPACING_4B};
  padding-right: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_4B};
  cursor: pointer;
  grid-column: span 6;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  border-radius: ${Spacings.SPACING_2B};
  border: ${({ $isSelected }) =>
    $isSelected
      ? `${Spacings.SPACING_1} solid var(--color-text-primary)`
      : `${Spacings.SPACING_1} solid var(--color-divider)`};
  background: var(--color-base-1);
  transition: background 300ms ease-in-out;
  transition: border 300ms ease-in-out;
  &:hover {
    ${({ $isUpcoming }) =>
      $isUpcoming
        ? 'cursor: not-allowed;'
        : `background: var(--color-base-light-0); border: ${Spacings.SPACING_1} solid var(--color-text-primary)`}
    ${mediaQuery.uptoMobile} {
      grid-column: span 24;
      width: 100%;
      margin-right: ${Spacings.SPACING_0B};
    }
  }
`;

const StyImage = styled(Image)`
  border-radius: ${Spacings.SPACING_2B};
  height: ${Spacings.SPACING_12B};
  width: ${Spacings.SPACING_12B};
  object-fit: cover;
`;

const StyH5 = styled(Typography)`
  line-height: ${Spacings.SPACING_6B};
`;
const StyP1 = styled(Typography)`
  color: var(--color-text-secondary);
  line-height: ${Spacings.SPACING_5B};
`;

const LeftDiv = styled.div`
  margin-right: ${Spacings.SPACING_4B};
  display: flex;
  align-items: center;
`;
const RightDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const LeftBlock = styled.div`
  display: flex;
`;
const RightBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Tag = styled(Typography)`
  background: ${({ $bgColor }) => $bgColor || 'var(--color-i-blue)'};
  padding: ${Spacings.SPACING_2} ${Spacings.SPACING_1B};
  border-radius: ${Spacings.SPACING_1B};
  display: inline;
  color: var(--color-i-white);
  color: ${({ $textColor }) => $textColor || 'var(--color-i-white)'};
  margin-right: ${Spacings.SPACING_0B};
  line-height: 100%;
  font-weight: ${Fonts.HEAVY};
  font-size: ${Spacings.SPACING_3B};
  transition: 0.3s ease;
  ${Container}:hover & {
    margin-right: ${Spacings.SPACING_4B};
  }
`;

const Arrow = styled.div`
  display: flex;
  opacity: 0;
  transition: 0.3s ease;
  margin-right: -16px;
  ${Container}:hover & {
    ${({ $isUpcoming }) =>
      $isUpcoming ? '' : 'opacity: 1;transform: translateX(-16px);'}
  }
`;

const CityCard = ({ data, goalUID, closeDrawer, isSelected }) => {
  const { uid: centreUID, tags, status, name: areaName } = data;
  let { icon_url: iconURL } = data;
  if (!iconURL) iconURL = data.iconUrl;
  const { topology: goalTopology } = useTopology(goalUID);
  const { goalSlug } = goalTopology;
  const {
    query: { goalUID: routeGoalUID }
  } = useRouter();
  const { centreListData } = useOfflineCentreStore();
  const userId = useMyInfo(getMeUid);
  const centreBEdata = centreListData.find(
    (centre) => centre.uid === centreUID
  );
  const { city, state, name: centreName, centreType } = centreBEdata;
  const handleClick = async () => {
    const { centreDeepLinkUrl } = await getOfflineCentreDetails(
      routeGoalUID,
      centreUID
    );
    if (userId) {
      const body = {
        goal_uid: routeGoalUID,
        user_uid: userId,
        selected_centre_uid: centreUID
      };
      await fetchOfflineSelectedCentre({ body });
    }

    const fallbackRoute = `/goal/${goalSlug}/${goalUID}/centre/${centreName}/${centreUID}?source=citesCentreGoalPage`;

    const centreCMSRoute = `${getPathName(
      centreDeepLinkUrl,
      fallbackRoute
    )}?source=citesCentreGoalPage`;
    const lps = segmentStorage.getItem('lps') || 'NA';
    segment.track(CENTRE_CITY_SELECTED, {
      centre_name: centreName,
      city,
      goal_uid: routeGoalUID,
      city_select_flow: OFFLINE_CENTRE_SOURCE,
      selected_city: city,
      default_city: 'kota',
      last_primary_source: lps,
      state,
      centre_type: centreType
    });
    if (lps) segmentStorage.deleteItem('lps');

    closeDrawer();
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/centre/[centreSlug]/[centreUID]',
      centreCMSRoute
    );
  };
  const isUpcoming = status === STATUS.UPCOMING;
  return (
    <Container
      onClick={handleClick}
      $isSelected={isSelected}
      $isUpcoming={isUpcoming}
    >
      <LeftBlock>
        <LeftDiv>
          <StyImage src={iconURL} alt="city-icon" height="48px" width="48px" />
        </LeftDiv>
        <RightDiv>
          <StyH5 variant="h5">{city}</StyH5>
          <StyP1 variant="p2">{areaName}</StyP1>
        </RightDiv>
      </LeftBlock>
      <RightBlock>
        {!!tags && (
          <Tag
            $textColor={tags?.[0]?.textColor}
            $bgColor={tags?.[0]?.bgColor}
            variant="h6"
          >
            {tags?.[0]?.name}
          </Tag>
        )}
        <Arrow $isUpcoming={isUpcoming}>
          <RightChevron />
        </Arrow>
      </RightBlock>
    </Container>
  );
};

export default CityCard;
